import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { PortageContactCombobox } from '@/components/ui/portage-contact-combobox'
import { useUpdatePendingPortageAffiliationContact } from '@/hooks/queries/usePortageCompanies'
import { toast } from 'sonner'

interface PortageChangeContactDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  portageCompanyId: string
  portageCompanyName?: string | null
  freelancerId: string
  currentContactId?: string | null
  currentContactLabel?: string | null
  /** freelance = wording suggestion ; team = « Ajouter un contact ». */
  addContactVariant?: 'freelancer' | 'team'
}

/**
 * Dialogue pour changer / ajouter le référent d'une affiliation portage encore en attente.
 */
export function PortageChangeContactDialog({
  open,
  onOpenChange,
  portageCompanyId,
  portageCompanyName,
  freelancerId,
  currentContactId,
  currentContactLabel,
  addContactVariant = 'freelancer',
}: PortageChangeContactDialogProps) {
  const { t } = useTranslation()
  const [contactId, setContactId] = useState<string | null>(currentContactId ?? null)
  const [contactLabel, setContactLabel] = useState<string | null>(currentContactLabel ?? null)
  const updateMutation = useUpdatePendingPortageAffiliationContact()

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setContactId(currentContactId ?? null)
      setContactLabel(currentContactLabel ?? null)
    }
    onOpenChange(next)
  }

  const handleSubmit = async () => {
    if (!contactId) return
    try {
      await updateMutation.mutateAsync({
        portageCompanyId,
        portageContactId: contactId,
        freelancerId,
      })
      toast.success(t('portageBanner.changeContactSuccess'))
      onOpenChange(false)
    } catch (error) {
      toast.error(
        error instanceof Error && error.message
          ? error.message
          : t('portageBanner.changeContactError')
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="z-21474830003" overlayClassName="z-21474830002">
        <DialogHeader>
          <DialogTitle>{t('portageBanner.changeContactTitle')}</DialogTitle>
          <DialogDescription>
            {t('portageBanner.changeContactDescription', {
              company:
                portageCompanyName?.trim() || t('portageBanner.companyFallback'),
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label>
            {t('freelancerOnboarding.portageContact.selectLabel')}{' '}
            <span className="text-red-500">*</span>
          </Label>
          <PortageContactCombobox
            portageCompanyId={portageCompanyId}
            value={contactId}
            selectedLabel={contactLabel}
            onChange={(id, label) => {
              setContactId(id)
              setContactLabel(label ?? null)
            }}
            addContactVariant={addContactVariant}
            portageCompanyName={portageCompanyName}
            allowAddContact
          />
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!contactId || updateMutation.isPending}
          >
            {updateMutation.isPending
              ? t('common.loading')
              : t('portageBanner.changeContactSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
